import { z } from "zod";

export const idFilter = z.guid().optional();

export const idsFilter = z.array(z.guid()).optional();

export const searchFilter = z.string().optional();

export const dateFilter = z.string().optional();

export const locationTypeSchema = z.enum(["street", "city", "department", "region"]);
export type LocationType = z.infer<typeof locationTypeSchema>;

export const placeLocationTypeSchema = z.enum(["street", "city"]);
export const administrativeAreaLocationTypeSchema = z.enum(["department", "region"]);

export const placeLocationFilterSchema = z.object({
  type: placeLocationTypeSchema,
  label: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  radius: z.number(),
  /** Set when the place came from Google, and only then: it is what re-reads the address there. */
  googlePlaceId: z.string().optional(),
});

/** Matched on the address's own `department_code` / `region_code`, an ISO 3166-2 code. */
export const administrativeAreaLocationFilterSchema = z.object({
  type: administrativeAreaLocationTypeSchema,
  label: z.string(),
  code: z.string(),
});

export const locationFilterSchema = z.discriminatedUnion("type", [
  placeLocationFilterSchema,
  administrativeAreaLocationFilterSchema,
]);

export const locationFiltersSchema = z.array(locationFilterSchema).optional();

export type PlaceLocationFilter = z.infer<typeof placeLocationFilterSchema>;
export type AdministrativeAreaLocationFilter = z.infer<typeof administrativeAreaLocationFilterSchema>;

export const isPlaceLocationFilter = (location: LocationFilter): location is PlaceLocationFilter =>
  location.type === "street" || location.type === "city";

/** What a location is known by in a list, since none of them carries an id. */
export const locationFilterKey = (location: LocationFilter): string =>
  isPlaceLocationFilter(location)
    ? (location.googlePlaceId ?? `${location.type}-${location.latitude}-${location.longitude}`)
    : `${location.type}-${location.code}`;

export type AddressLocation = { formatted: string; latitude: number; longitude: number };

export const keywordFilterSchema = z.object({
  operator: z.enum(["or", "and"]),
  value: z.string(),
});
export type KeywordFilter = z.infer<typeof keywordFilterSchema>;

export const keywordFilterGroupSchema = z.object({
  operator: z.enum(["or", "and"]),
  values: z.array(z.string()),
});
export type KeywordFilterGroup = z.infer<typeof keywordFilterGroupSchema>;

export const listParams = z.object({
  pageIndex: z.number().optional(),
  pageSize: z.number().optional(),
  orderColumn: z.string().optional(),
  orderDirection: z.enum(["asc", "desc"]).optional(),
});

export const listResponse = z.object({
  totalItems: z.number(),
});

export type ListParams = z.infer<typeof listParams>;
export type LocationFilter = z.infer<typeof locationFilterSchema>;
export type LocationsFilter = z.infer<typeof locationFiltersSchema>;
